import { useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastProps {
  id: string;
  type: ToastType;
  message: string;
  onDismiss: (id: string) => void;
  duration?: number;
}

const toastStyles: Record<ToastType, { bg: string; icon: typeof CheckCircle; iconColor: string }> = {
  success: {
    bg: 'bg-light-green',
    icon: CheckCircle,
    iconColor: 'text-green-700',
  },
  error: {
    bg: 'bg-red-100',
    icon: AlertCircle,
    iconColor: 'text-red-600',
  },
  info: {
    bg: 'bg-soft-cyan',
    icon: Info,
    iconColor: 'text-cyan-700',
  },
};

export function Toast({ id, type, message, onDismiss, duration = 4000 }: ToastProps) {
  const style = toastStyles[type];
  const Icon = style.icon;

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onDismiss(id);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [id, duration, onDismiss]);

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg ${style.bg} animate-slide-in`}
      role="alert"
    >
      <Icon className={`w-5 h-5 flex-shrink-0 ${style.iconColor}`} />
      <p className="text-sm font-medium text-text-primary flex-1">{message}</p>
      <button
        onClick={() => onDismiss(id)}
        className="p-1 hover:bg-black/10 rounded transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4 text-text-secondary" />
      </button>
    </div>
  );
}

export interface ToastData {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContainerProps {
  toasts: ToastData[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          id={toast.id}
          type={toast.type}
          message={toast.message}
          duration={toast.duration}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
}
